import { Compartment, EditorState, RangeSetBuilder, type Extension, type TransactionSpec } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  RectangleMarker,
  ViewPlugin,
  drawSelection,
  highlightActiveLineGutter,
  keymap,
  layer,
  lineNumbers,
  placeholder,
  type DecorationSet,
  type KeyBinding,
  type ViewUpdate,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { HighlightStyle, syntaxHighlighting, syntaxTree, indentUnit } from '@codemirror/language';
import { markdownKeymap, markdownLanguage } from '@codemirror/lang-markdown';
import { tags as t } from '@lezer/highlight';
import type { DocKind } from '../core/documents';
import { DEFAULT_PREFS, type EditorPrefs } from '../platform/prefs';
import { outdentLines, tabSpec } from './indent';
import { applyLink, isProbablyUrl, setBlockStyle, toggleInline, toggleList, toggleQuote, type BlockStyle } from './markdownCommands';

/**
 * Editor configuration. Each window has one EditorView that displays its
 * active document; each document keeps its own EditorState (and so its own
 * undo history). Kind-specific behaviour lives in a compartment so a draft
 * can switch between Markdown and plain text without losing history; display
 * preferences live in another so they can change for every document at once.
 */

export const kindCompartment = new Compartment();
export const prefsCompartment = new Compartment();

type UpdateHandler = (update: ViewUpdate) => void;
let updateHandler: UpdateHandler | null = null;
let linkRequestHandler: ((view: EditorView) => void) | null = null;
let prefs: EditorPrefs = DEFAULT_PREFS;

/** Route editor updates (from every view) to the app controller. */
export function onEditorUpdate(handler: UpdateHandler): void {
  updateHandler = handler;
}

/** Called when Mod-K is pressed over selected text in a Markdown document. */
export function onLinkRequest(handler: (view: EditorView) => void): void {
  linkRequestHandler = handler;
}

export function runSpec(view: EditorView, make: (state: EditorState) => TransactionSpec | null, userEvent = 'input.format'): boolean {
  const spec = make(view.state);
  if (!spec) return false;
  view.dispatch({ ...spec, userEvent, scrollIntoView: true });
  return true;
}

const formatKeymap: KeyBinding[] = [
  { key: 'Mod-b', run: (v) => runSpec(v, (s) => toggleInline(s, 'bold')) },
  { key: 'Mod-i', run: (v) => runSpec(v, (s) => toggleInline(s, 'italic')) },
  { key: 'Mod-Shift-x', run: (v) => runSpec(v, (s) => toggleInline(s, 'strike')) },
  { key: 'Mod-e', run: (v) => runSpec(v, (s) => toggleInline(s, 'code')) },
  // ⌘K links selected words; with nothing selected it falls through to the command palette.
  { key: 'Mod-k', run: (v) => (!v.state.selection.main.empty && linkRequestHandler ? (linkRequestHandler(v), true) : false) },
  ...([0, 1, 2, 3] as BlockStyle[]).map((level) => ({ key: `Mod-Alt-${level}`, run: (v: EditorView) => runSpec(v, (s) => setBlockStyle(s, level)) })),
  { key: 'Mod-Shift-7', run: (v) => runSpec(v, (s) => toggleList(s, 'ordered')) },
  { key: 'Mod-Shift-8', run: (v) => runSpec(v, (s) => toggleList(s, 'bullet')) },
  { key: 'Mod-Shift-9', run: (v) => runSpec(v, toggleQuote) },
];

/** Classes rather than colours: the stylesheet owns both themes. */
const markdownHighlight = HighlightStyle.define([
  { tag: t.heading1, class: 'md-h md-h1' },
  { tag: t.heading2, class: 'md-h md-h2' },
  { tag: t.heading3, class: 'md-h md-h3' },
  { tag: [t.heading4, t.heading5, t.heading6], class: 'md-h md-h4' },
  { tag: t.strong, class: 'md-strong' },
  { tag: t.emphasis, class: 'md-em' },
  { tag: t.strikethrough, class: 'md-strike' },
  { tag: t.processingInstruction, class: 'md-mark' },
  { tag: t.monospace, class: 'md-code' },
  { tag: t.link, class: 'md-link' },
  { tag: t.url, class: 'md-url' },
  { tag: [t.labelName, t.string], class: 'md-meta' },
  { tag: t.quote, class: 'md-quote' },
  { tag: t.contentSeparator, class: 'md-rule' },
  { tag: [t.comment, t.meta], class: 'md-meta' },
]);

const fencedLine = Decoration.line({ class: 'md-fence' });
const fencedFirst = Decoration.line({ class: 'md-fence md-fence-first' });
const fencedLast = Decoration.line({ class: 'md-fence md-fence-last' });
const fencedOnly = Decoration.line({ class: 'md-fence md-fence-first md-fence-last' });
const headingLines = [1, 2, 3, 4, 5, 6].map((n) => Decoration.line({ class: `md-heading md-hang-${n}` }));
const codeChip = Decoration.mark({ class: 'md-chip' });

/**
 * Block-level typesetting for Markdown source: fenced code reads as one quiet
 * block, inline code as one chip, and heading lines carry the class that lets
 * their `#` marks hang in the margin so heading text aligns with body text.
 */
const markdownBlocks = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = this.build(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged || syntaxTree(u.startState) !== syntaxTree(u.state)) this.decorations = this.build(u.view);
    }
    build(view: EditorView): DecorationSet {
      const builder = new RangeSetBuilder<Decoration>();
      const { doc } = view.state;
      let lastLine = 0;
      const line = (n: number, deco: Decoration) => {
        if (n <= lastLine) return;
        lastLine = n;
        builder.add(doc.line(n).from, doc.line(n).from, deco);
      };
      for (const { from, to } of view.visibleRanges) {
        syntaxTree(view.state).iterate({
          from,
          to,
          enter(node) {
            if (node.name === 'FencedCode') {
              const first = doc.lineAt(node.from).number;
              const last = doc.lineAt(node.to).number;
              for (let n = Math.max(first, doc.lineAt(from).number); n <= Math.min(last, doc.lineAt(to).number); n++) {
                line(n, first === last ? fencedOnly : n === first ? fencedFirst : n === last ? fencedLast : fencedLine);
              }
              return false;
            }
            const heading = /^ATXHeading(\d)$/.exec(node.name);
            // Only a heading that begins its line can hang (not one inside a quote or list).
            if (heading && doc.lineAt(node.from).from === node.from) {
              line(doc.lineAt(node.from).number, headingLines[Number(heading[1]) - 1]);
            } else if (node.name === 'InlineCode') {
              builder.add(node.from, node.to, codeChip);
              return false;
            }
          },
        });
      }
      return builder.finish();
    }
  },
  { decorations: (v) => v.decorations },
);

/**
 * Measures how wide each heading level's `#` marks are in the current fonts
 * and theme, and exposes the widths as CSS variables used to hang them.
 */
const hangingMarks = ViewPlugin.fromClass(
  class {
    private probe: HTMLElement;
    private observer: MutationObserver;
    private onFonts = () => this.measure();
    constructor(private view: EditorView) {
      this.probe = document.createElement('div');
      this.probe.className = 'md-probe';
      this.probe.setAttribute('aria-hidden', 'true');
      this.probe.innerHTML = [1, 2, 3, 4, 5, 6].map((n) => `<div class="md-probe-line"><span class="md-h md-h${Math.min(n, 4)} md-mark">${'#'.repeat(n)}</span><span class="md-h md-h${Math.min(n, 4)}"> </span></div>`).join('');
      view.dom.appendChild(this.probe);
      this.observer = new MutationObserver(() => this.measure());
      this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      document.fonts?.addEventListener('loadingdone', this.onFonts);
      this.measure();
    }
    update(u: ViewUpdate) {
      if (u.geometryChanged) this.measure();
    }
    measure() {
      this.view.requestMeasure({
        key: this,
        read: () => Array.from(this.probe.children).map((line) => (line as HTMLElement).scrollWidth),
        write: (widths, view) => {
          widths.forEach((w, i) => view.dom.style.setProperty(`--hang-${i + 1}`, `${w}px`));
        },
      });
    }
    destroy() {
      this.observer.disconnect();
      document.fonts?.removeEventListener('loadingdone', this.onFonts);
      this.probe.remove();
    }
  },
);

/** Pasting a URL over selected text makes a link instead of replacing the words. */
const pasteLinks = EditorView.domEventHandlers({
  paste(event, view) {
    const text = event.clipboardData?.getData('text/plain') ?? '';
    const sel = view.state.selection.main;
    if (sel.empty || view.state.selection.ranges.length > 1 || !isProbablyUrl(text)) return false;
    if (view.state.doc.sliceString(sel.from, sel.to).includes('\n')) return false;
    event.preventDefault();
    return runSpec(view, (s) => applyLink(s, text), 'input.paste');
  },
});

/**
 * Tab and Shift-Tab indent and outdent — while the "Tab indents" preference
 * is on. Off, the keys are left alone and move focus like anywhere else on
 * the page; they never replace or delete the selection.
 */
function indentKeymap(kind: DocKind): Extension {
  return keymap.of([
    {
      key: 'Tab',
      run: (v) => prefs.tabIndent && runSpec(v, (s) => tabSpec(s, kind), 'input.indent'),
      shift: (v) => {
        if (!prefs.tabIndent) return false;
        runSpec(v, (s) => outdentLines(s, kind), 'delete.dedent');
        return true;
      },
    },
  ]);
}

function kindExtensions(kind: DocKind): Extension {
  if (kind === 'txt') {
    return [EditorView.editorAttributes.of({ class: 'is-txt' }), indentKeymap('txt'), placeholder('Plain text. Start writing.')];
  }
  return [
    EditorView.editorAttributes.of({ class: 'is-md' }),
    indentKeymap('md'),
    keymap.of(formatKeymap),
    // The bare language (GFM flavour) without embedded HTML/JS/CSS grammars: MyTXT highlights prose, not code.
    markdownLanguage,
    keymap.of(markdownKeymap),
    syntaxHighlighting(markdownHighlight),
    markdownBlocks,
    hangingMarks,
    pasteLinks,
    placeholder('Start writing.'),
  ];
}

export function reconfigureKind(kind: DocKind) {
  return kindCompartment.reconfigure(kindExtensions(kind));
}

/**
 * The current line, drawn as one soft band behind the text. It is a single
 * element that moves (and can glide) rather than a class that jumps from line
 * to line. In wrapped prose it marks the visual row, not the whole paragraph.
 */
const lineBand = layer({
  above: false,
  class: 'cm-lineband-layer',
  update: (u) => u.selectionSet || u.docChanged || u.geometryChanged || u.focusChanged || u.viewportChanged,
  markers(view) {
    const sel = view.state.selection;
    if (!view.hasFocus || !sel.main.empty || sel.ranges.length > 1) return [];
    const head = sel.main.head;
    const caret = view.coordsAtPos(head, sel.main.assoc || 1) ?? view.coordsAtPos(head, -1);
    if (!caret) return [];
    const scroller = view.scrollDOM.getBoundingClientRect();
    const content = view.contentDOM.getBoundingClientRect();
    const baseLeft = scroller.left - view.scrollDOM.scrollLeft;
    const baseTop = scroller.top - view.scrollDOM.scrollTop;
    const block = view.lineBlockAt(head);
    const lh = view.defaultLineHeight;
    let top: number;
    let height: number;
    if (block.height < lh * 1.6) {
      top = view.documentTop + block.top;
      height = block.height;
    } else {
      const glyph = caret.bottom - caret.top;
      const pad = Math.max(0, (lh - glyph) / 2);
      top = caret.top - pad;
      height = glyph + 2 * pad;
    }
    const style = getComputedStyle(view.contentDOM);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const bleed = 10;
    return [new RectangleMarker('cm-lineband', content.left + padLeft - baseLeft - bleed, top - baseTop, content.width - padLeft - padRight + 2 * bleed, height)];
  },
});

function prefsExtensions(p: EditorPrefs): Extension {
  return [
    p.lineNumbers ? [lineNumbers(), p.currentLine ? highlightActiveLineGutter() : [], EditorView.editorAttributes.of({ class: 'has-line-numbers' })] : [],
    p.currentLine ? lineBand : [],
  ];
}

/** Apply new display preferences to documents created from now on, and return the effect for existing ones. */
export function setEditorPrefs(next: EditorPrefs) {
  prefs = next;
  return prefsCompartment.reconfigure(prefsExtensions(next));
}

/** Selection and caret colours come from the product's tokens, at CodeMirror's specificity. */
const theme = EditorView.theme({
  '&': { height: '100%', backgroundColor: 'transparent' },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': { fontFamily: 'inherit', lineHeight: 'inherit' },
  '.cm-cursor, .cm-dropCursor': { borderLeft: '2px solid var(--caret)', marginLeft: '-1px', borderRadius: '1px' },
  '.cm-selectionBackground': { background: 'var(--selection-blur) !important' },
  '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground': { background: 'var(--selection) !important' },
  '.cm-content ::selection': { backgroundColor: 'transparent !important' },
  '.cm-placeholder': { color: 'var(--ink-4)' },
});

/** Files dropped on the text open as documents instead of being pasted in. */
const fileDrops = EditorView.domEventHandlers({
  drop: (event) => !!event.dataTransfer?.files.length,
});

const shared: Extension = [
  theme,
  fileDrops,
  history(),
  drawSelection({ cursorBlinkRate: 1100 }),
  EditorView.lineWrapping,
  EditorState.allowMultipleSelections.of(true),
  indentUnit.of('  '),
  EditorState.tabSize.of(4),
  keymap.of([...defaultKeymap, ...historyKeymap]),
  EditorView.contentAttributes.of({ spellcheck: 'true', autocapitalize: 'sentences', 'aria-label': 'Document text' }),
  // Keep the caret clear of the floating pill and the tab head while typing.
  EditorView.scrollMargins.of(() => ({ top: 24, bottom: 112 })),
  EditorView.updateListener.of((u) => updateHandler?.(u)),
];

export function createDocState(text: string, kind: DocKind, selection?: { anchor: number; head: number }): EditorState {
  const length = text.length;
  const clamp = (n: number) => Math.max(0, Math.min(length, n));
  return EditorState.create({
    doc: text,
    selection: selection ? { anchor: clamp(selection.anchor), head: clamp(selection.head) } : undefined,
    extensions: [kindCompartment.of(kindExtensions(kind)), prefsCompartment.of(prefsExtensions(prefs)), shared],
  });
}
